import { useCallback, useEffect, useState } from "react";
import { Home, Radio, Phone, Camera, Satellite, Tv } from "lucide-react";
import { useLang } from "../context/LanguageContext";

// Floating mini-nav that appears while the user is scrolling through the
// Parceiros section. Lets visitors jump between the 4 comparators, the
// DSTV showcase, and instantly back to the top of the page.

const HOME_LABEL = {
  pt: "Início",
  en: "Top",
  es: "Inicio",
};

// Items are listed in DOM/scroll order so the scrollspy loop picks the
// correct active item as the user scrolls down. DSTV showcase appears
// before VSAT in `Partners.jsx`, so it must come before VSAT here too.
const ITEMS = {
  pt: [
    { id: "radio-compare", label: "Rádios", icon: Radio },
    { id: "intelbras-compare", label: "Centrais", icon: Phone },
    { id: "cctv-compare", label: "CCTV", icon: Camera },
    { id: "dstv", label: "DSTV", icon: Tv },
    { id: "vsat-compare", label: "VSAT", icon: Satellite },
  ],
  en: [
    { id: "radio-compare", label: "Radios", icon: Radio },
    { id: "intelbras-compare", label: "PBX", icon: Phone },
    { id: "cctv-compare", label: "CCTV", icon: Camera },
    { id: "dstv", label: "DSTV", icon: Tv },
    { id: "vsat-compare", label: "VSAT", icon: Satellite },
  ],
  es: [
    { id: "radio-compare", label: "Radios", icon: Radio },
    { id: "intelbras-compare", label: "Centralitas", icon: Phone },
    { id: "cctv-compare", label: "CCTV", icon: Camera },
    { id: "dstv", label: "DSTV", icon: Tv },
    { id: "vsat-compare", label: "VSAT", icon: Satellite },
  ],
};

// Bookends used to compute whether the floating nav should be visible.
const RANGE_START_ID = "partners";
const RANGE_END_ID = "vsat";

// How far above the start section we trigger the reveal, and how far past
// the end section we keep it visible. Tuned so the nav fades in just as the
// brand marquee appears and fades out once the last showcase is scrolled.
const PRE_OFFSET_PX = 300;
const POST_OFFSET_PX = 200;

export const PartnersQuickNav = () => {
  const { lang } = useLang();
  const items = ITEMS[lang] || ITEMS.pt;
  const [visible, setVisible] = useState(false);
  const [active, setActive] = useState("");

  const recompute = useCallback(() => {
    const start = document.getElementById(RANGE_START_ID);
    const end = document.getElementById(RANGE_END_ID);
    if (!start || !end) {
      setVisible(false);
      return;
    }
    const y = window.scrollY;
    const startY = start.offsetTop - PRE_OFFSET_PX;
    const endY = end.offsetTop + end.offsetHeight - POST_OFFSET_PX;
    const shouldShow = y >= startY && y < endY;
    setVisible(shouldShow);

    if (!shouldShow) return;
    // Scrollspy: pick the section whose top is closest to (but above) the
    // upper third of the viewport.
    const probeY = window.innerHeight * 0.35;
    let current = "";
    for (const item of items) {
      const el = document.getElementById(item.id);
      if (!el) continue;
      const top = el.getBoundingClientRect().top;
      if (top <= probeY) current = item.id;
    }
    setActive(current);
  }, [items]);

  useEffect(() => {
    recompute();
    const onScroll = () => recompute();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [recompute]);

  const goTo = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const goToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div
      data-testid="partners-quick-nav"
      aria-hidden={!visible}
      className={`pointer-events-none fixed inset-x-0 bottom-6 z-40 flex justify-center px-3 transition-all duration-300 md:bottom-8 ${
        visible
          ? "translate-y-0 opacity-100"
          : "pointer-events-none translate-y-4 opacity-0"
      }`}
    >
      <nav
        aria-label="Quick navigation"
        className={`pointer-events-auto flex max-w-[calc(100vw-1.5rem)] items-center gap-1 overflow-x-auto rounded-full border border-white/10 bg-slate-900/85 px-2 py-2 shadow-2xl backdrop-blur-md ${
          visible ? "" : "pointer-events-none"
        }`}
        style={{ scrollbarWidth: "none" }}
      >
        {/* Back-to-top quick action — always first */}
        <button
          type="button"
          onClick={goToTop}
          data-testid="quick-nav-home"
          aria-label={HOME_LABEL[lang] || HOME_LABEL.pt}
          className="group mr-1 inline-flex shrink-0 items-center gap-1.5 rounded-full bg-white/10 px-4 py-2.5 text-[13px] font-bold text-white transition-all hover:-translate-y-0.5 hover:bg-white/20 md:text-[15px]"
        >
          <Home className="h-4 w-4 shrink-0 md:h-5 md:w-5" strokeWidth={2.4} />
          <span className="whitespace-nowrap">{HOME_LABEL[lang] || HOME_LABEL.pt}</span>
        </button>

        <span aria-hidden className="h-6 w-px shrink-0 bg-white/15" />

        {items.map((it) => {
          const Icon = it.icon;
          const isActive = active === it.id;
          return (
            <button
              key={it.id}
              type="button"
              onClick={() => goTo(it.id)}
              data-testid={`quick-nav-${it.id}`}
              aria-current={isActive ? "true" : undefined}
              className={`group inline-flex shrink-0 items-center gap-1.5 rounded-full px-4 py-2.5 text-[13px] font-semibold transition-all md:text-[15px] ${
                isActive
                  ? "bg-blue-500 text-white shadow-md"
                  : "text-slate-200 hover:bg-white/10 hover:text-white"
              }`}
            >
              <Icon
                className={`h-4 w-4 shrink-0 transition-colors md:h-5 md:w-5 ${
                  isActive ? "text-white" : "text-slate-300 group-hover:text-white"
                }`}
                strokeWidth={2.2}
              />
              <span className="whitespace-nowrap">{it.label}</span>
            </button>
          );
        })}
      </nav>
    </div>
  );
};

export default PartnersQuickNav;
